/**
 * This hook is used to manage application data in local storage.
 * It provides functions to update and get any value in a flat json blob.
 */

import { useCallback } from 'react';

export function useApplicationLocalStorage<T>(key: string) {
  const getApplicationData = useCallback(() => {
    if (typeof window === 'undefined') {
      return {};
    }
    const storedData = localStorage.getItem(key);
    return storedData ? JSON.parse(storedData) : {};
  }, [key]);

  const updateApplicationData = useCallback(
    (newData: Partial<T>) => {
      const applicationData = getApplicationData();
      const updatedData = { ...applicationData, ...newData };

      //Avoid localStorage access on the server side as it is not defined
      if (typeof window !== 'undefined') {
        console.log(`Updating application data for key: ${key} with data:`, newData);
        localStorage.setItem(key, JSON.stringify(updatedData));
      }
    },
    [getApplicationData, key],
  );

  const getApplicationDataByKey = useCallback(
    (key: keyof T) => {
      const applicationData = getApplicationData();
      return applicationData[key];
    },
    [getApplicationData],
  );

  const clearApplicationData = useCallback(() => {
    if (typeof window !== 'undefined') {
      console.log(`Clearing application data for key: ${key}`);
      localStorage.removeItem(key);
    }
  }, [key]);

  return {
    getApplicationData,
    updateApplicationData,
    getApplicationDataByKey,
    clearApplicationData,
  };
}
